import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import CalendarDays from './CalendarDays';
import CalendarLinkButton from './CalendarLinkButton';
import CalendarNavButton from './CalendarNavButton';
import CalendarTitleButton from './CalendarTitleButton';
import CalendarMonths from './CalendarMonths';
import CalendarYears from './CalendarYears';
import { useTempEdgeTranslator } from '../../../Hooks/useTranslation';
import {
  YEARS_PER_PAGE,
  getMonthNames,
  getToday,
  getTodayLabel,
  getYearPageLabel,
  getYearPageStart,
  isOutOfRange
} from '../../../utils/calendar';

const NAV_STEP = { days: [1, 'months'], months: [1, 'years'], years: [YEARS_PER_PAGE, 'years'] };

const DateCalendar = ({ selected, viewDate, minDate, maxDate, locale, onSelect, onViewChange, onClear, testId = 'calendar-panel' }) => {
  const { tempedgeTranslator } = useTempEdgeTranslator();
  const [mode, setMode] = useState('days');
  const monthNames = useMemo(() => getMonthNames(locale), [locale]);
  const year = viewDate.getFullYear();
  const pageStart = getYearPageStart(year);
  const isTodayDisabled = isOutOfRange(getToday(), minDate, maxDate);

  const move = (direction) => {
    const [amount, unit] = NAV_STEP[mode];
    onViewChange(
      moment(viewDate)
        .add(direction * amount, unit)
        .toDate()
    );
  };

  const pickPeriod = (unit, nextMode) => (value) => {
    onViewChange(moment(viewDate)[unit](value).toDate());
    setMode(nextMode);
  };

  return (
    <div className="te-calendar" role="dialog" data-testid={testId}>
      <div className="te-calendar-head">
        <CalendarNavButton
          step={-1}
          label={tempedgeTranslator('com.tempedge.msg.label.previous')}
          testId="calendar-nav-prev"
          icon={FaChevronLeft}
          onMove={move}
        />
        <div className="te-calendar-titles">
          {mode === 'days' && (
            <CalendarTitleButton targetMode="months" testId="calendar-title-month" onSelectMode={setMode}>
              {monthNames[viewDate.getMonth()]}
            </CalendarTitleButton>
          )}
          {mode === 'years' ? (
            <span className="te-calendar-title is-static">{getYearPageLabel(pageStart)}</span>
          ) : (
            <CalendarTitleButton targetMode="years" testId="calendar-title-year" onSelectMode={setMode}>
              {year}
            </CalendarTitleButton>
          )}
        </div>
        <CalendarNavButton
          step={1}
          label={tempedgeTranslator('com.tempedge.msg.label.next')}
          testId="calendar-nav-next"
          icon={FaChevronRight}
          onMove={move}
        />
      </div>

      {mode === 'days' && (
        <CalendarDays viewDate={viewDate} selected={selected} minDate={minDate} maxDate={maxDate} locale={locale} onSelect={onSelect} />
      )}
      {mode === 'months' && (
        <CalendarMonths
          viewDate={viewDate}
          selected={selected}
          minDate={minDate}
          maxDate={maxDate}
          locale={locale}
          onPick={pickPeriod('month', 'days')}
        />
      )}
      {mode === 'years' && (
        <CalendarYears
          pageStart={pageStart}
          selected={selected}
          minDate={minDate}
          maxDate={maxDate}
          onPick={pickPeriod('year', 'months')}
        />
      )}

      <div className="te-calendar-foot">
        {onClear && (
          <CalendarLinkButton label={tempedgeTranslator('com.tempedge.msg.label.clean')} testId="calendar-clear" onClick={onClear} />
        )}
        <CalendarLinkButton
          label={getTodayLabel(locale)}
          testId="calendar-today"
          onClick={() => onSelect(getToday())}
          disabled={isTodayDisabled}
          primary
        />
      </div>
    </div>
  );
};

export default DateCalendar;
